// bluaIQ — P1: RFI Module
// Request for Information tracking with priority and response workflow

import { apiFetch } from '../utils/api'
import { formatDateOnly, dateOnlyKey } from '../utils/dateOnly'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, HelpCircle, AlertCircle, ChevronDown, ChevronUp, Download } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import { downloadCsv } from '../utils/exportCsv'

// ── TYPES ─────────────────────────────────────────

interface RFI {
  id: string
  subject: string
  question: string
  assignedTo: string
  priority: string
  status: string
  response?: string
  dueDate: string | null
  createdAt: string
}

const PRIORITY_STYLES: Record<string, { bg: string; text: string }> = {
  LOW:    { bg: '#F2F0EB', text: '#9E9890' },
  MEDIUM: { bg: '#EEF2FF', text: '#06539B' },
  HIGH:   { bg: '#FEF3C7', text: '#D97706' },
  URGENT: { bg: '#FEF2F2', text: '#EF4444' },
}
const PRIORITY_KEYS = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  OPEN:     { bg: '#EEF2FF', text: '#06539B' },
  ANSWERED: { bg: '#ECFDF5', text: '#22C55E' },
  CLOSED:   { bg: '#F2F0EB', text: '#9E9890' },
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_RFIS: RFI[] = [
  {
    id: 'rfi-1', subject: 'Foundation rebar spacing', question: 'Plans show #4 rebar at 12" OC but soil report recommends 8" OC. Which spec to follow?',
    assignedTo: 'Structural Engineer', priority: 'HIGH', status: 'ANSWERED',
    response: 'Follow soil report recommendation. Use #4 @ 8" OC for all footings. Updated plans attached.',
    dueDate: '2026-03-15', createdAt: '2026-03-10',
  },
  {
    id: 'rfi-2', subject: 'Electrical panel location', question: 'Garage wall selected for panel conflicts with planned EV charger outlet. Alternate location?',
    assignedTo: 'Spark Electric Co', priority: 'MEDIUM', status: 'OPEN',
    dueDate: '2026-03-28', createdAt: '2026-03-20',
  },
  {
    id: 'rfi-3', subject: 'Window header size at master bedroom', question: '8-foot window opening — need confirmation on header size. LVL 3.5x11.875 or doubled 2x12?',
    assignedTo: 'Mendez Framing LLC', priority: 'HIGH', status: 'OPEN',
    dueDate: '2026-03-25', createdAt: '2026-03-18',
  },
  {
    id: 'rfi-4', subject: 'HVAC duct routing in hallway', question: 'Hallway ceiling height is 8\'. Can we route supply duct through attic instead of soffit?',
    assignedTo: 'Comfort Air HVAC', priority: 'LOW', status: 'CLOSED',
    response: 'Approved. Route through attic with R-8 insulated flex duct. Maintain fire dampers at wall penetrations.',
    dueDate: '2026-03-12', createdAt: '2026-03-05',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function RFIModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [showForm, setShowForm] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data: apiData, isLoading } = useQuery<RFI[]>({
    queryKey: ['rfis', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/rfis`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const rfis = Array.isArray(apiData) ? apiData : (isDemoProject(projectId) ? DEMO_RFIS : [])

  const open = rfis.filter(r => r.status === 'OPEN')
  const now = new Date()
  // PR3 (C2/C5): overdue = the due DAY is before today. An RFI without a due
  // date is never overdue (new Date(null) is 1970 → it used to count).
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const isPastDue = (r: RFI) => { const k = dateOnlyKey(r.dueDate); return !!k && k < todayKey }
  const overdue = open.filter(isPastDue)
  const answered = rfis.filter(r => r.status === 'ANSWERED' || r.status === 'CLOSED')
  // PR3: RFIs without a due date used to turn this into NaN.
  const answeredWithDue = answered.filter(r => !!r.dueDate)
  const avgResponseDays = answeredWithDue.length > 0
    ? Math.round(answeredWithDue.reduce((s, r) => s + Math.max(1, Math.round((new Date(r.dueDate as string).getTime() - new Date(r.createdAt).getTime()) / 86400000)), 0) / answeredWithDue.length)
    : 0

  // PR3 (C5): due date is a calendar day.
  const formatDate = (d?: string | null) => formatDateOnly(d, isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">RFIs</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {rfis.length} {t('rfi.requestsLabel')} · {open.length} {t('rfi.openLabel')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rfis.length > 0 && (
            <button
              onClick={() => downloadCsv({
                filename: `rfis-${projectId}`,
                headers: [t('rfi.csv.subject'), t('rfi.csv.assignedTo'), t('rfi.csv.priority'), t('rfi.csv.status'), t('rfi.csv.dueDate'), t('rfi.csv.created')],
                rows: rfis.map(r => [r.subject, r.assignedTo, r.priority, r.status, r.dueDate, r.createdAt])
              })}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
            >
              <Download size={14} />
              CSV
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full"
          >
            <Plus size={14} />
            {t('rfi.newRfi')}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className={`bg-white border rounded-xl p-3 ${open.length > 0 ? 'border-[#06539B]/30' : 'border-[#ECEAE4]'}`}>
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('rfi.kpiOpen')}</div>
          <div className="text-[18px] font-[900] text-[#06539B]">{open.length}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{t('rfi.requestsLabel')}</div>
        </div>
        <div className={`bg-white border rounded-xl p-3 ${overdue.length > 0 ? 'border-red-300' : 'border-[#ECEAE4]'}`}>
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('rfi.kpiOverdue')}</div>
          <div className={`text-[18px] font-[900] ${overdue.length > 0 ? 'text-[#EF4444]' : 'text-[#1C1A17]'}`}>{overdue.length}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{t('rfi.kpiNeedAttention')}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('rfi.kpiAvgResponse')}</div>
          <div className="text-[18px] font-[900] text-[#1C1A17]">{avgResponseDays} {t('rfi.kpiDays')}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{t('rfi.kpiResponseTime')}</div>
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <AddRFIForm
          projectId={projectId!}
          onClose={() => setShowForm(false)}
          onAdded={() => { setShowForm(false); queryClient.invalidateQueries({ queryKey: ['rfis', projectId] }) }}
        />
      )}

      {/* RFI list */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#ECEAE4]">
          <h3 className="text-[13px] font-[800]">{t('rfi.allRfis')}</h3>
        </div>

        {rfis.length === 0 && (
          <div className="text-center py-12">
            <HelpCircle size={36} className="mx-auto text-[#ECEAE4] mb-3" />
            <p className="text-[12px] font-[700] text-[#9E9890]">
              {t('rfi.noRfis')}
            </p>
          </div>
        )}

        {rfis.map(rfi => {
          const statusStyle = STATUS_STYLES[rfi.status] || STATUS_STYLES.OPEN
          const priorityStyle = PRIORITY_STYLES[rfi.priority] || PRIORITY_STYLES.LOW
          const isExpanded = expanded === rfi.id
          const isOverdue = rfi.status === 'OPEN' && isPastDue(rfi)

          return (
            <div key={rfi.id} className="border-t border-[#ECEAE4]">
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
                onClick={() => setExpanded(isExpanded ? null : rfi.id)}
              >
                {isOverdue && <AlertCircle size={14} className="text-[#EF4444] flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-[800] text-[#1C1A17]">{rfi.subject}</span>
                    <span className="text-[8px] font-[800] px-1.5 py-0.5 rounded-full" style={{ background: statusStyle.bg, color: statusStyle.text }}>
                      {t(`rfi.status.${rfi.status}`, { defaultValue: rfi.status })}
                    </span>
                    <span className="text-[8px] font-[800] px-1.5 py-0.5 rounded-full" style={{ background: priorityStyle.bg, color: priorityStyle.text }}>
                      {t(`rfi.priority.${rfi.priority}`, { defaultValue: rfi.priority })}
                    </span>
                  </div>
                  <div className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
                    {rfi.assignedTo} · {t('rfi.due')} {formatDate(rfi.dueDate)}
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={14} className="text-[#9E9890]" /> : <ChevronDown size={14} className="text-[#9E9890]" />}
              </div>

              {isExpanded && (
                <div className="px-4 pb-3 pl-8 space-y-2">
                  <div>
                    <div className="text-[10px] font-[800] text-[#9E9890] mb-1">{t('rfi.question')}</div>
                    <p className="text-[11px] font-[600] text-[#1C1A17]">{rfi.question}</p>
                  </div>
                  {rfi.response && (
                    <div className="bg-[#ECFDF5] rounded-lg p-3 mt-2">
                      <div className="text-[10px] font-[800] text-[#22C55E] mb-1">{t('rfi.response')}</div>
                      <p className="text-[11px] font-[600] text-[#1C1A17]">{rfi.response}</p>
                    </div>
                  )}
                  {!isDemoProject(projectId) && (
                    <RFIAnswerBox
                      projectId={projectId!}
                      rfi={rfi}
                      isEs={isEs}
                      onSaved={() => queryClient.invalidateQueries({ queryKey: ['rfis', projectId] })}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── ADD FORM ─────────────────────────────────────

function AddRFIForm({ projectId, onClose, onAdded }: {
  projectId: string; onClose: () => void; onAdded: () => void
}) {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const [form, setForm] = useState({ subject: '', question: '', assignedTo: '', priority: 'MEDIUM', dueDate: '' })

  const mutation = useMutation({
    mutationFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/rfis`, {
      method: 'POST',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, status: 'OPEN' })
    }).then(async r => {
      // PR3 (C2): this used to swallow every 400 and close as if it saved.
      if (!r.ok) throw new Error(`rfi_create_failed_${r.status}`)
      return r.json()
    }) },
    onSuccess: onAdded
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <h3 className="text-[13px] font-[800]">{t('rfi.newRfi')}</h3>
      <input placeholder={t('rfi.form.subject')} value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      <textarea placeholder={t('rfi.form.question')} value={form.question} onChange={e => setForm({ ...form, question: e.target.value })}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B] h-[60px] resize-none" />
      <div className="grid grid-cols-3 gap-2">
        <input placeholder={t('rfi.form.assignedTo')} value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
        <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]">
          {PRIORITY_KEYS.map((key) => (
            <option key={key} value={key}>{t(`rfi.priority.${key}`)}</option>
          ))}
        </select>
        <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      </div>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
        <button onClick={() => mutation.mutate()} disabled={!form.subject || !form.question || mutation.isPending}
          className="flex-1 bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40">
          {mutation.isPending ? '...' : t('Save')}
        </button>
      </div>
      {mutation.isError && (
        <p role="alert" data-testid="rfi-create-error" className="text-[11px] font-[700] text-red-700">
          {isEs ? 'No se pudo guardar el RFI. Revisa los datos e intenta de nuevo.' : 'Could not save the RFI. Check the fields and try again.'}
        </p>
      )}
    </div>
  )
}

// ── ANSWER / CLOSE (PR3, C2) ─────────────────────
// Before PR3 an RFI could be created but never answered or closed.

function RFIAnswerBox({ projectId, rfi, isEs, onSaved }: {
  projectId: string; rfi: RFI; isEs: boolean; onSaved: () => void
}) {
  const { getToken } = useAuth()
  const [response, setResponse] = useState(rfi.response ?? '')

  const mutation = useMutation({
    mutationFn: async (body: { response?: string; status?: string }) => {
      const token = await getToken()
      const r = await apiFetch(`/api/projects/${projectId}/rfis/${rfi.id}`, {
        method: 'PATCH',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(`rfi_update_failed_${r.status}`)
      return r.json()
    },
    onSuccess: onSaved,
  })

  if (rfi.status === 'CLOSED') {
    return (
      <button
        onClick={() => mutation.mutate({ status: 'OPEN' })}
        disabled={mutation.isPending}
        className="text-[10px] font-[800] text-[#06539B] underline disabled:opacity-40"
      >
        {isEs ? 'Reabrir' : 'Reopen'}
      </button>
    )
  }

  return (
    <div className="space-y-2 pt-1" data-testid="rfi-answer-box">
      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        placeholder={isEs ? 'Escribe la respuesta…' : 'Write the answer…'}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B] h-[60px] resize-none"
      />
      <div className="flex gap-2">
        <button
          onClick={() => mutation.mutate({ response: response.trim() })}
          disabled={!response.trim() || mutation.isPending}
          className="flex-1 bg-[#06539B] text-white rounded-full py-1.5 text-[11px] font-[800] disabled:opacity-40"
        >
          {isEs ? 'Guardar respuesta' : 'Save answer'}
        </button>
        <button
          onClick={() => mutation.mutate({ status: 'CLOSED' })}
          disabled={mutation.isPending}
          className="flex-1 border border-[#06539B] text-[#06539B] rounded-full py-1.5 text-[11px] font-[800] disabled:opacity-40"
        >
          {isEs ? 'Cerrar RFI' : 'Close RFI'}
        </button>
      </div>
      {mutation.isError && (
        <p role="alert" className="text-[11px] font-[700] text-red-700">
          {isEs ? 'No se pudo guardar. Intenta de nuevo.' : 'Could not save. Try again.'}
        </p>
      )}
    </div>
  )
}
